import axios, { AxiosError } from "axios";
import { useCallback, useContext } from "react";
import { AuthContext } from "../../../context/AuthContext";
import { MMMonitoringEndpointEnum } from "../../../enums/endpoints";
import { MM_MONITORING_API_URL } from "../../../utils/env";
import { ToastEnum, ToastStatusEnum, useToast } from "../../toast";

export const useMMMonitoring = () => {
  const { tokenId } = useContext(AuthContext);
  const { addToast } = useToast();

  const withAuthHeaders = useCallback(
    () => ({
      headers: {
        Authorization: `Bearer ${tokenId}`,
      },
    }),
    [tokenId]
  );

  const handleSuccess = useCallback(
    (header: string, subheader?: string) => {
      addToast(
        {
          type: ToastEnum.SIMPLE,
          header,
          subheader,
          status: ToastStatusEnum.SUCCESS,
        },
        4000
      );
    },
    [addToast]
  );

  const handleError = useCallback(
    (error: AxiosError, header: string) => {
      // eslint-disable-next-line no-console
      console.error(error);
      addToast(
        {
          type: ToastEnum.SIMPLE,
          header,
          subheader: ((error as AxiosError)?.response?.data as any)?.error,
          status: ToastStatusEnum.ERROR,
        },
        4000
      );
    },
    [addToast]
  );

  const startMmProcess = useCallback(
    async (process: string, asset: string, env: string) => {
      try {
        await axios.post(
          MM_MONITORING_API_URL(MMMonitoringEndpointEnum.START),
          { process, asset, env },
          withAuthHeaders()
        );
        handleSuccess("MM Process started", `${asset} - ${process} (${env})`);
      } catch (e) {
        handleError(e as AxiosError, "Start MM Process failed");
      }
    },
    [handleError, handleSuccess, withAuthHeaders]
  );

  const stopMmProcess = useCallback(
    async (process: string, asset: string, env: string) => {
      try {
        await axios.post(
          MM_MONITORING_API_URL(MMMonitoringEndpointEnum.STOP),
          { process, asset, env },
          withAuthHeaders()
        );
        handleSuccess("MM Process stopped", `${asset} - ${process} (${env})`);
      } catch (e) {
        handleError(e as AxiosError, "Stop MM Process failed");
      }
    },
    [handleError, handleSuccess, withAuthHeaders]
  );

  const restartMmProcess = useCallback(
    async (process: string, asset: string, env: string) => {
      try {
        await axios.post(
          MM_MONITORING_API_URL(MMMonitoringEndpointEnum.RESTART),
          { process, asset, env },
          withAuthHeaders()
        );
        handleSuccess("MM Process restarted", `${asset} - ${process} (${env})`);
      } catch (e) {
        handleError(e as AxiosError, "Restart MM Process failed");
      }
    },
    [handleError, handleSuccess, withAuthHeaders]
  );

  const getProcessStatus = useCallback(
    async (process: string, env: string, instance?: string, silent: boolean = false) => {
      try {
        let url = `${MM_MONITORING_API_URL(MMMonitoringEndpointEnum.STATUS)}?process=${process}&env=${env}`;
        if (instance) {
          url += `&instance=${instance}`;
        }
        const response = await axios.get(
          url,
          withAuthHeaders()
        );
        return response.data.data;
      } catch (e) {
        if (!silent) {
          handleError(e as AxiosError, "Failed to fetch process status");
        }
        return null;
      }
    },
    [handleError, withAuthHeaders]
  );

  const stopAllProcesses = useCallback(
    async (process: string, env: string) => {
      try {
        await axios.post(
          MM_MONITORING_API_URL(MMMonitoringEndpointEnum.STOP_ALL),
          { process, env },
          withAuthHeaders()
        );
        handleSuccess("All processes stopped", `${process} (${env})`);
      } catch (e) {
        handleError(e as AxiosError, "Stop all processes failed");
      }
    },
    [handleError, handleSuccess, withAuthHeaders]
  );

  const restartAllProcesses = useCallback(
    async (process: string, env: string) => {
      try {
        await axios.post(
          MM_MONITORING_API_URL(MMMonitoringEndpointEnum.RESTART_ALL),
          { process, env },
          withAuthHeaders()
        );
        handleSuccess("All processes restarted", `${process} (${env})`);
      } catch (e) {
        handleError(e as AxiosError, "Restart all processes failed");
      }
    },
    [handleError, handleSuccess, withAuthHeaders]
  );

  return {
    startMmProcess,
    stopMmProcess,
    restartMmProcess,
    getProcessStatus,
    stopAllProcesses,
    restartAllProcesses,
  };
};
